import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { api, formatApiError } from "../lib/api";
import PasswordStrength, { scorePassword } from "../components/PasswordStrength";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Invalid reset link.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    const strength = scorePassword(password);
    if (strength.score < 3) return toast.error("Pick a stronger password.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Reset failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#080808", color: "#F5F5F5", position: "relative" }}>
      <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%, -50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(249,115,22,0.08), transparent 70%)", pointerEvents: "none" }} />
      <header className="px-6 py-5 flex items-center justify-between max-w-7xl mx-auto w-full">
        <Link to="/"><Logo size="sm" forceDark /></Link>
        <div className="text-sm text-gray-500"><Link to="/login" className="text-[#F97316] font-medium">Back to sign in</Link></div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="p-8 space-y-5" style={{ background: "#111111", border: "1px solid rgba(249,115,22,0.2)", borderRadius: 16 }}>
            {done ? (
              <div className="text-center space-y-4">
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(16,185,129,0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <CheckCircle2 style={{ color: "#10b981" }} size={28} />
                </div>
                <h1 className="text-2xl font-bold font-display">Password updated</h1>
                <p className="text-sm text-gray-400">Redirecting you to sign in…</p>
              </div>
            ) : (
              <>
                <div>
                  <h1 className="text-2xl font-bold font-display">Set a new password</h1>
                  <p className="text-sm text-gray-500 mt-1">Choose a strong password you haven't used elsewhere.</p>
                </div>
                {!token && (
                  <div style={{ padding: 12, borderRadius: 8, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", fontSize: 13, color: "#fca5a5" }}>
                    No reset token found. Request a new link from <Link to="/forgot-password" className="underline">forgot password</Link>.
                  </div>
                )}
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-300">New password</label>
                    <div className="mt-1 relative">
                      <input
                        required
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="8+ characters"
                        autoComplete="new-password"
                        style={{ background: "#1A1A1A", color: "#F5F5F5" }}
                        className="w-full border border-gray-600 rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
                      />
                      <button type="button" onClick={() => setShowPw((v) => !v)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "#9ca3af", cursor: "pointer", padding: 4 }}>
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <PasswordStrength password={password} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300">Confirm password</label>
                    <input
                      required
                      type={showPw ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Re-type password"
                      autoComplete="new-password"
                      style={{ background: "#1A1A1A", color: "#F5F5F5" }}
                      className="mt-1 w-full border border-gray-600 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
                    />
                  </div>
                  <button disabled={busy || !token} className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white rounded-lg font-medium disabled:opacity-50">
                    {busy ? "Updating…" : "Update password"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
